import { setActiveTrace } from '@atlaskit/react-ufo/experience-trace-id-context';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { perf } from '@databases/logs/performance';
import { proxy } from 'valtio';

import { buildWorkspaceAri, setAtlAttributionDataGetter } from '@canvas/atl-attribution';
import { updateFrameContext } from '@canvas/canvas-state/host-api';
import { updateSiteId } from '@canvas/canvas-state/site-data';
import { Deferrable } from '@canvas/deferrable';
import { transformToken } from '@canvas/frame-api/canvas-frame-api';
import { type RawFrameToken } from '@canvas/frame-api-types';
import type { CanvasIdentity } from '@canvas/model/identity';

import { setActivationId, setCloudId } from '../gateway.ts';

/**
 * Identity profile sent from CFE, it has a similar shape to {@link CanvasIdentity} but a
 * few differences:
 * * It currently sends the user's `displayName` but calls it `nickname`
 * * `type` is missing since it's application-specific context
 */
export type FrameIdentity = Omit<CanvasIdentity, 'type' | 'displayName'>;

/**
 * Tracing context propagated from the parent Confluence experience.
 * Used to correlate database iframe API calls with the parent UFO trace.
 */
export type TracingContext = {
  traceId: string;
  spanId: string;
};

export type BridgeData = {
  /**
   * A unique ID for content across Atlassian.
   * Used for analytics events.
   */
  activationId: string;

  /**
   * AKA the tenant id.
   */
  cloudId: string;

  /**
   * The ID of the database, on Confluence. (Uses page-tree in the greater Confluence ecosystem)
   */
  contentId: string;

  /**
   * The ID of the database on Canvas.
   */
  contentReferenceId: string;

  /**
   * Identity profile sent from CFE, it has a similar shape to CanvasIdentity but a few
   * differences:
   *
   * It currently sends the user's displayName but calls it nickname
   * type is missing since it's application-specific context
   */
  identity: FrameIdentity;

  /**
   * The unique ID for the confluence space.
   */
  spaceId: string;

  /**
   * The URL-friendly slug for the confluence space.
   */
  spaceKey: string;

  /**
   * The confluence space name. Need to check if this is used correctly...
   */
  spaceName: string;

  /**
   * The title of the confluence document (contentID). Canvas is not aware of the title as
   * it is stored on the confluence.
   */
  title: string;

  /**
   * A collection of tokens for authentication.
   *
   * Used for communication with other services. See `RawFrameToken`.
   */
  token: RawFrameToken;

  /**
   * @deprecated Use `identity.accountId` instead.
   */
  userId: string;

  /**
   * Tracing context from the parent Confluence experience.
   * Optional — will be undefined until Confluence starts sending it.
   */
  tracingContext?: TracingContext;
};

export type BridgeDataWithReferencing = BridgeData & { referencing: boolean };

type BridgeDataState = {
  data: BridgeData;
};

export const currentBridgeData = proxy<BridgeDataState>({
  data: {
    activationId: '',
    cloudId: '',
    contentId: '',
    contentReferenceId: '',
    identity: {
      accountId: '',
      nickname: '',
      picture: '',
      email: '',
      locale: ''
    },
    spaceId: '',
    spaceKey: '',
    spaceName: '',
    title: '',
    token: {
      container: '',
      board: '',
      accessToken: '',
      mediaToken: '',
      mediaBaseUrl: '',
      mediaCollectionId: '',
      policies: { dataSecurityPolicy: { pageExport: undefined } }
    },
    userId: ''
  }
});

// frameContextReady resolves when setBridgeData() is first called
// await bridgeDataReady.get() if there's a chance that currentBridgeData may not be set yet
export const bridgeDataReady = new Deferrable<BridgeData>();

let bridgeDataMutationObserver: MutationObserver | null = null;

const setBridgeData = (rawBridgeData: string | null | undefined) => {
  if (!rawBridgeData) {
    return;
  }

  try {
    const parsedBridgeData = JSON.parse(rawBridgeData) as BridgeData;
    if (parsedBridgeData) {
      perf.timeLog('app-initialization', 'bridge-data');

      currentBridgeData.data = parsedBridgeData;
      updateExternalStateFromBridgeData(parsedBridgeData);
      bridgeDataReady.resolve(currentBridgeData.data);
    }

    return parsedBridgeData;
  } catch (e) {
    throw new Error('Bridge data are not valid JSON');
  }
};

/**
 * Sets the bridge data for a development mode that is not mounted in an iframe.
 * Currently, only the Profiler mode satisfies this condition.
 */
const setProfilerModeBridgeData = () => {
  const params = new URLSearchParams(window.location.search);
  const rawData = params.get('bridgeData') as string;

  setBridgeData(rawData);

  const parsed = JSON.parse(rawData);

  GlobalPageLoadExperience.mark('load-bridge-end');

  return parsed as BridgeData;
};

/**
 * When the bridge data changes, some external state needs to be updated.
 */
const updateExternalStateFromBridgeData = (bridgeData: BridgeData) => {
  setActivationId(bridgeData.activationId);
  setCloudId(bridgeData.cloudId);
  updateSiteId(bridgeData.cloudId);
  updateFrameContext(transformToken(bridgeData.token));
  setAtlAttributionDataGetter(() => ({
    atlWorkspaceId: buildWorkspaceAri(bridgeData.cloudId, bridgeData.activationId),
    tenantId: bridgeData.cloudId,
    product: 'Confluence Cloud',
    service: 'micros/nct-databases'
  }));
  if (bridgeData.tracingContext) {
    setActiveTrace(
      bridgeData.tracingContext.traceId,
      bridgeData.tracingContext.spanId,
      ''
    );
  }
};

function readReferencingFromEmbedSearch(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return new URLSearchParams(window.location.search).get('referencing') === 'true';
}

const buildBridgeDataFromEnvVars = (): BridgeData | null => {
  const databaseId = process.env.VITE_LOCAL_DATABASE_ID;
  const asapToken = process.env.VITE_LOCAL_ASAP_TOKEN;
  const siteId = process.env.VITE_LOCAL_SITE_ID;
  const activationId = process.env.VITE_LOCAL_ACTIVATION_ID;
  const accountId = process.env.VITE_LOCAL_ACCOUNT_ID;
  const spaceId = process.env.VITE_LOCAL_SPACE_ID;

  if (!databaseId || !asapToken || !siteId || !activationId) {
    return null;
  }

  return {
    activationId,
    cloudId: siteId,
    contentId: `${Math.round(Math.random() * 100000000)}`,
    contentReferenceId: databaseId,
    identity: {
      accountId: accountId ?? '',
      nickname: 'Local Developer',
      picture: '',
      email: 'local@dev.test',
      locale: 'en-US'
    },
    spaceId: spaceId ?? '',
    spaceKey: 'LOCAL',
    spaceName: 'Local Development',
    title: 'Local Database',
    token: {
      container: siteId,
      board: `ari:cloud:canvas:${siteId}:database/${databaseId}`,
      accessToken: asapToken,
      mediaToken: '',
      mediaBaseUrl: '',
      mediaCollectionId: '',
      policies: { dataSecurityPolicy: { pageExport: true } }
    },
    userId: accountId ?? ''
  };
};

export const initializeBridgeData = async (): Promise<BridgeDataWithReferencing> => {
  const referencing = readReferencingFromEmbedSearch();

  GlobalPageLoadExperience.mark('load-bridge-start');
  if (process.env.NODE_ENV === 'development') {
    const envBridgeData = buildBridgeDataFromEnvVars();
    if (envBridgeData) {
      setBridgeData(JSON.stringify(envBridgeData));

      GlobalPageLoadExperience.mark('load-bridge-end');

      return { ...envBridgeData, referencing };
    }

    if (window.location.search.includes('bridgeData')) {
      const data = setProfilerModeBridgeData();

      return { ...data, referencing };
    }
  }

  setBridgeData(window.frameElement?.getAttribute('data-bridge-data'));

  bridgeDataMutationObserver?.disconnect();
  bridgeDataMutationObserver = null;

  bridgeDataMutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes') {
        const rawBridgeData = window.frameElement?.getAttribute('data-bridge-data');

        if (rawBridgeData) {
          setBridgeData(rawBridgeData);
        }
      }
    });
  });

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  bridgeDataMutationObserver.observe(window.frameElement!, {
    attributes: true,
    attributeFilter: ['data-bridge-data']
  });

  const response = await bridgeDataReady.get();

  GlobalPageLoadExperience.mark('load-bridge-end');

  return { ...response, referencing };
};
